import React from 'react';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { VersionForm } from 'src/features/reservations/components/modals/VersionForm';
import {
    createReservationTypeFormSchema,
    type CreateReservationTypeFormSchemaValues,
} from 'src/features/reservations/hooks/useCreateReservationType';
import { useUpdateReservationType } from 'src/features/reservations/hooks/useUpdateReservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { ReservationType } from 'src/features/reservations/types/reservationType';

type EditVersionModalProps = {
    isEditVersionModalOpen: boolean;
    closeEditVersionModal: (isOpen: boolean) => void;
    selectedVersion: ReservationType;
    refetch: () => void;
};

const EditVersionModal = ({
    isEditVersionModalOpen,
    closeEditVersionModal,
    selectedVersion,
    refetch,
}: EditVersionModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();

    const form = useForm<CreateReservationTypeFormSchemaValues>({
        resolver: zodResolver(createReservationTypeFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    const { isDirty, isValid } = form.formState;

    React.useEffect(() => {
        if (selectedVersion) {
            form.reset({
                reservation_type: selectedVersion.reservation_type,
                valid_from: new Date(selectedVersion.valid_from),
                price: selectedVersion.price,
                tax_amount: selectedVersion.tax_amount,
                tax_percent: selectedVersion.tax_percent,
                price_with_tax: selectedVersion.price_with_tax,
                currency: selectedVersion.currency,
                allowed_spaces: selectedVersion.allowed_spaces,
                entry_tolerance_minutes: selectedVersion.entry_tolerance_minutes,
                exit_tolerance_minutes: selectedVersion.exit_tolerance_minutes,
                reserve_space: selectedVersion.reserve_space,
                name: selectedVersion.name,
                description: selectedVersion.description,
                points: selectedVersion.points || [],
            });
        }
    }, [selectedVersion]);

    const { mutate: editVersion } = useUpdateReservationType({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(reservationMessages.editVersionSuccess),
                description: formatMessage(reservationMessages.editVersionSuccess),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { tax_percent, price_with_tax } = form.watch();

    const calculatedPrice = React.useMemo(() => {
        return price_with_tax ? Math.round(price_with_tax / (1 + Number(tax_percent))) : 0;
    }, [price_with_tax, tax_percent]);

    const taxAmountCalculation = React.useMemo(() => {
        return price_with_tax && calculatedPrice ? price_with_tax - calculatedPrice : 0;
    }, [price_with_tax, calculatedPrice]);

    const onSubmit = (values: CreateReservationTypeFormSchemaValues) => {
        const dirtyFields = form.formState.dirtyFields;

        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof CreateReservationTypeFormSchemaValues;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Partial<Record<keyof CreateReservationTypeFormSchemaValues, any>>,
        );
        if (dirtyFields.tax_percent || dirtyFields.price_with_tax) {
            editedFields.price = calculatedPrice;
            editedFields.tax_amount = taxAmountCalculation;
        }
        const submitData = {
            parkingId: parkingId || '',
            versionId: selectedVersion.id,
            ...editedFields,
        };
        editVersion(submitData);
        closeEditVersionModal(!isEditVersionModalOpen);
        form.reset();
    };

    return (
        <Dialog open={isEditVersionModalOpen} onOpenChange={closeEditVersionModal}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.editVersion)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.editVersionDetails)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <VersionForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!isDirty || !isValid}
                    >
                        {formatMessage(reservationMessages.editVersion)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditVersionModal;
